import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Avatar,
  Box,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';

const AuthorForm = ({ open, onClose, onSubmit, editData, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    avatar: '',
    bio: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  React.useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        avatar: editData.avatar || '',
        bio: editData.bio || ''
      });
    } else {
      setFormData({
        name: '',
        avatar: '',
        bio: ''
      });
    }
  }, [editData, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting author:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const uploadFileToFilesAPI = async (file) => {
    const user = JSON.parse(localStorage.getItem("user"))
    const form = new FormData();
    form.append('ownerId', user._id);
    form.append('type', 'file');
    form.append('file', file);
    form.append('ownerEmail', user.email);

    const uploadUrl = `https://api.files.clikkle.com/api/fs/create?id=${user._id}`;
    const resp = await axios.post(uploadUrl, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const fileId = resp?.data?.record?._id;
    if (!fileId) {
      throw new Error('Upload failed: file id missing');
    }
    setFormData(prev => ({ ...prev, avatar: `https://api.files.clikkle.com/api/fs/access/${fileId}?userId=${user._id}` }));
    return `https://api.files.clikkle.com/api/fs/access/${fileId}?userId=${user._id}`;
  };

  const handleAvatarFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingAvatar(true);
    try {
      const accessUrl = await uploadFileToFilesAPI(file);
      setFormData(prev => ({ ...prev, avatar: accessUrl }));
    } catch (err) {
      console.error('Avatar upload error:', err);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {isEditing ? 'Edit Author' : 'Create New Author'}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.name}
                onChange={handleChange("name")}
                required
                helperText="The author's full name as it should appear on blog posts"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Avatar URL"
                value={formData.avatar}
                onChange={handleChange("avatar")}
                placeholder="https://example.com/avatar.jpg"
                helperText="URL for the author's profile picture"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                component="label"
                disabled={isUploadingAvatar}
              >
                {isUploadingAvatar ? 'Uploading...' : 'Upload Avatar Image'}
                <input type="file" hidden accept="image/*" onChange={handleAvatarFileSelect} />
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                value={formData.bio}
                onChange={handleChange("bio")}
                multiline
                rows={3}
                helperText="Brief biography of the author"
              />
            </Grid>

            {formData.avatar && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                  <Avatar
                    src={formData.avatar}
                    sx={{ width: 80, height: 80 }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Author' : 'Create Author')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AuthorForm;